import { DocumentChangeInterface, Expense, OperationTypeEnum } from "@finway-group/shared/lib/models"
import i18n from "i18next"
import store, { ThunkDispatchResult } from "Shared/store"
import { fetchDashboardExpensesCounts, setShouldRefetchExpenses, syncExpense } from "Shared/store/actions/expense/expenseActions"
import { refetchExpenseTables } from "Shared/store/actions/tables/tableActions"
import NotificationService, { NotificationTypeEnum } from "../notification.service"
import { downloadFileAsSignedUrl } from "Shared/utils/helpers"

/**
 * If someone else modified an expense, dispatch updates
 */
export const subscribeToExpenses = async (dispatch: ThunkDispatchResult, data: DocumentChangeInterface) => {
    // prettier-ignore
    const { fullDocument: { documentId, operationType, metaData } } = data;

    // expense store item
    const currentOpenedExpense = store.getState().expenses.item?.id

    switch (operationType) {
        case OperationTypeEnum.INSERT:
        case OperationTypeEnum.INSERT_COMMENT:
        case OperationTypeEnum.UPDATE_COMMENT:
        case OperationTypeEnum.DELETE_COMMENT:
        case OperationTypeEnum.BULK_UPDATE:
        case OperationTypeEnum.DATEV_ONLINE_EXPORT:
        case OperationTypeEnum.DELETE:
        case OperationTypeEnum.UPLOAD_DOCUMENT:
        case OperationTypeEnum.REMOVE_DOCUMENT:
        case OperationTypeEnum.APPROVE_EXPENSE:
        case OperationTypeEnum.DECLINED_EXPENSE:
        case OperationTypeEnum.REVIEWED_EXPENSE:
        case OperationTypeEnum.PAID_EXPENSE:
        case OperationTypeEnum.DOCUMENT_EXPENSE:
        case OperationTypeEnum.DOCUMENT_NEEDED_EXPENSE:
            // on mass updates or a new insert, refetch the store
            dispatch(refetchExpenseTables())
            setShouldRefetchExpenses(true)(dispatch)
            dispatch(fetchDashboardExpensesCounts())
            if (currentOpenedExpense) syncExpense(currentOpenedExpense)(dispatch)
            break
        case OperationTypeEnum.ATTACH_TAG:
        case OperationTypeEnum.DETACH_TAG:
            if (currentOpenedExpense) syncExpense(currentOpenedExpense)(dispatch)
            break
        default:
            if ((metaData as any).updatedFields?.status) dispatch(fetchDashboardExpensesCounts())
            const expenseState = store.getState().expenses
            // for updates, refetch if it's present in the store
            const expense = expenseState.item?.id === documentId ? expenseState.item : expenseState.items.find(({ id }: Expense) => id === documentId)
            if (expense && documentId) {
                await syncExpense(documentId.toString())(dispatch)
            }
            break
    }
}

/**
 * For async actions for the same user who initiated it
 */
export const subscribeToExpenseCallbacks = (dispatch: ThunkDispatchResult, data: DocumentChangeInterface) => {
    // prettier-ignore
    const { fullDocument: { operationType, metaData } } = data;

    // expense store item
    const currentOpenedExpense = store.getState().expenses.item?.id

    switch (operationType) {
        case OperationTypeEnum.EXPENSE_CARD_CREATION_SUCCESS:
            NotificationService.send(
                NotificationTypeEnum.SUCCESS,
                i18n.t("notification:cards.expense_card_creation.title"),
                i18n.t("notification:cards.expense_card_creation.message")
            )
            break
        case OperationTypeEnum.EXPENSE_CARD_CREATION_FAILED:
            NotificationService.send(NotificationTypeEnum.ERROR, i18n.t("error:error"), i18n.t("error:cards.expense_card_creation"), 30)
            break
        case OperationTypeEnum.INSERT:
        case OperationTypeEnum.PAID_EXPENSE:
        case OperationTypeEnum.BULK_UPDATE:
        case OperationTypeEnum.DATEV_ONLINE_EXPORT:
        case OperationTypeEnum.DELETE:
        case OperationTypeEnum.APPROVE_EXPENSE:
        case OperationTypeEnum.DECLINED_EXPENSE:
        case OperationTypeEnum.REVIEWED_EXPENSE:
        case OperationTypeEnum.PAID_EXPENSE:
        case OperationTypeEnum.DOCUMENT_EXPENSE:
        case OperationTypeEnum.DOCUMENT_NEEDED_EXPENSE:
            // Required to update expense view when a payment has been successful when checkAndNotify returns false
            dispatch(fetchDashboardExpensesCounts())
            // table store
            dispatch(refetchExpenseTables())
            // expense store items
            setShouldRefetchExpenses(true)(dispatch)
            // Perform sync to the currently opened expense
            if (currentOpenedExpense) syncExpense(currentOpenedExpense)(dispatch)
            break
        case OperationTypeEnum.DATEV_ONLINE_EXPORT_WRONG_IMPORT_TARGET:
            NotificationService.send(NotificationTypeEnum.ERROR, i18n.t("error:datev_online.wrong_target.title"), i18n.t("error:datev_online.wrong_target.message"), 0)
            break
        case OperationTypeEnum.DATEV_ONLINE_EXPORT_ERROR:
            NotificationService.send(NotificationTypeEnum.WARNING, i18n.t("error:datev_online.export_error.title"), i18n.t("error:datev_online.export_error.message"), 0)
            break
        case OperationTypeEnum.ATTACH_TAG:
        case OperationTypeEnum.DETACH_TAG:
            if (currentOpenedExpense) syncExpense(currentOpenedExpense)(dispatch)
            break
        case OperationTypeEnum.ASYNC_EXPORT:
            NotificationService.send(NotificationTypeEnum.SUCCESS, i18n.t("notification:request.exported_bulk.title"), i18n.t("notification:request.exported_bulk.message"), 0)
            downloadFileAsSignedUrl((metaData as any).fileUrl)
            break
        default:
            break
    }
}

export default subscribeToExpenses
